import {RouterLink} from "@angular/router";
import {AfterViewInit, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";

import {appRoutes} from '@numizmat/constants';
import {environment} from "@numizmat/environments";
import {PasswordRecoveryApiService} from "@numizmat/services/account";
import {LogoComponent, PageHeaderComponent} from "@numizmat/components";
import {PasswordRecoveryRequest} from "@numizmat/services-models/account";
import {JsonLdService, MetaTagService, ScrollerService} from "@numizmat/services/common";

@Component({
  selector: 'nz-password-recovery',
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    LogoComponent
  ],
  templateUrl: './password-recovery.component.html',
  styleUrl: './password-recovery.component.scss'
})
export class PasswordRecoveryComponent implements OnInit, OnDestroy, AfterViewInit {
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private scrollerService: ScrollerService = inject(ScrollerService);
  private passwordRecoveryApiService: PasswordRecoveryApiService = inject(PasswordRecoveryApiService);

  protected readonly appRoutes = appRoutes;

  protected loading: boolean = false;
  protected success: boolean = false;
  protected errorMessage: string = '';
  protected form: FormGroup = this.initializeForm();

  public ngOnInit(): void {
    this.setSchema();
    this.setMetaTags();
  }

  public ngAfterViewInit(): void {
    this.scrollerService.scrollToTop();
  }

  public ngOnDestroy(): void {
    this.jsonLdService.removeSchema();
  }

  protected onSubmit(): void {
    this.loading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.loading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: PasswordRecoveryRequest = {
      email: formValue.email,
    };
    this.passwordRecoveryApiService.passwordRecovery(request).subscribe({
      next: response => {
        this.loading = false;
        if (response.success) {
          this.success = true;
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  private initializeForm() {
    return new FormGroup({
      email: new FormControl(null, [Validators.required]),
    });
  }

  private setSchema(): void {
    const passwordRecoverySchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Забравена парола",
      "description": "Възстановете достъпа до профила си в Collectoria, като въведете своя имейл адрес.",
      "url": `${environment.applicationDomain}/${appRoutes.account}/${appRoutes.passwordRecovery}`
    };
    this.jsonLdService.setSchema(passwordRecoverySchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.account}/${appRoutes.passwordRecovery}`);
    this.metaTagService.setTitle('Възстановяване на парола | Collectoria');
    this.metaTagService.setDescription('Възстановяване на парола | Collectoria');
  }

  get email() {
    return this.form.get('email');
  }
}
